import { Spacing } from '@persona-id/design-tokens';
import { RotateIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';

import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import PlatformSchemaMappings, {
  InputConfig,
} from 'dashboard/components/WorkflowControlPanel/PlatformSchemaMappings';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateStepContext } from 'dashboard/lib/workflows';
import { PlatformJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { BlockInput } from 'dashboard/models/workflow-block';
import { WorkflowStepModule } from 'dashboard/models/workflow-version';
import { setStepContexts } from 'dashboard/redux/actions/workflow';
import {
  useGetWorkflowBlockQuery,
  useLazyGetWorkflowBlockQuery,
} from 'dashboard/redux/api/workflow-block';
import { useListWorkflowModulesQuery } from 'dashboard/redux/api/workflow-module';
import { getWorkflowSchema } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack/Stack';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useBatchQuery from 'lib/hooks/useBatchQuery';
import useEventBus from 'lib/hooks/useEventBus';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { JsonSchema } from 'lib/json-schema';

const getInputConfigs = (
  blockInputs: BlockInput[],
  inputMappings: WorkflowStepModule['input-mappings'],
) => {
  return blockInputs.map(({ token, name, schema, required }) => {
    const jsonSchema: PlatformJsonSchema = JSON.parse(schema);
    const inputMapping = inputMappings.find((mapping) => mapping['input-token'] === token);
    return {
      key: token,
      label: name,
      field: inputMapping ? inputMapping['method-chain'] : null,
      value: inputMapping ? inputMapping['raw-value'] : null,
      schema: jsonSchema,
      required,
    };
  });
};

interface WorkflowConfigurationModuleProps {
  editable: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  step: WorkflowStepModule;
  tokensToDisplayValue: Record<string, string>;
  trigger?: Trigger;
  updateStep: (newStep: WorkflowStepModule, skipContext?: boolean) => void;
}

const StepConfigModule: FC<WorkflowConfigurationModuleProps> = ({
  editable,
  errors,
  jsonSchema,
  step,
  tokensToDisplayValue,
  trigger,
  updateStep,
}) => {
  const showErrorObjects = useFeatureFlag('global:workflow-error-variables');

  const modulesListQuery = useListWorkflowModulesQuery({
    filter: { status: ['active'], with_published_block: true },
    page: {
      number: 1,
      size: 100,
    },
  });
  const blockQuery = useGetWorkflowBlockQuery(step['child-workflow-block-id'], {
    skip: !step['child-workflow-block-id'],
  });
  const [getBlock, blockLazyQuery] = useLazyGetWorkflowBlockQuery();
  const workflowSchema = useSelector(getWorkflowSchema);
  const dispatch = useDispatch();

  const { isLoadingAll } = useBatchQuery({
    queries: [modulesListQuery, blockQuery],
    onError: ({ requestId }) => {
      switch (requestId) {
        case modulesListQuery.requestId:
          return { message: 'Failed to load modules.' };
        case blockQuery.requestId:
          return { message: 'Failed to load module version.' };
      }
    },
  });

  const { data: modulesResponse, refetch: refetchModules } = modulesListQuery;
  const modulesList = useMemo(() => modulesResponse?.workflowModules || [], [modulesResponse]);

  const moduleOptions = useMemo(
    () =>
      modulesList.map((module) => ({
        label: module.attributes.name,
        value: module.id,
      })),
    [modulesList],
  );

  const { data: blockResponse, isFetching: isFetchingBlock } = blockQuery;
  const selectedModule = useMemo(
    () =>
      modulesResponse?.workflowModules?.find(
        ({ id }) => id === blockResponse?.data?.relationships?.parent?.data?.id,
      ) ?? null,
    [modulesResponse, blockResponse],
  );
  const isLatestBlock = useMemo(
    () =>
      selectedModule?.relationships['latest-published-block'].data?.id === blockResponse?.data.id,
    [selectedModule, blockResponse],
  );

  const inputConfigs = useMemo(() => {
    if (!blockResponse) {
      return [];
    }

    return getInputConfigs(blockResponse.data.attributes.inputs, step['input-mappings']);
  }, [blockResponse, step]);

  const updateWorkflowBlockId = useCallback(
    async (newModuleId: string) => {
      const newModule = modulesList.find(({ id }) => id === newModuleId) ?? null;
      const latestBlockId = newModule?.relationships['latest-published-block'].data?.id;
      const newStep: WorkflowStepModule = {
        ...step,
        name: newModule?.attributes.name ?? 'Unknown Module',
        'child-workflow-block-id': latestBlockId || '',
      };
      if (latestBlockId) {
        const { data: latestBlock } = await getBlock(latestBlockId, true).unwrap();
        const inputConfigs = getInputConfigs(
          latestBlock.attributes.inputs,
          step['input-mappings'],
        ).filter(({ field, value }) => field != null || value != null);
        newStep['input-mappings'] = inputConfigs.map(({ key, field, value }) => ({
          'input-token': key,
          'method-chain': field,
          'raw-value': value,
        }));
        dispatch(
          setStepContexts({
            [newStep.token]: generateStepContext({
              step: newStep,
              criteriaVariableSchemas: workflowSchema['criteria-variable-schemas'],
              genericWorkflowActions: workflowSchema['generic-workflow-actions'],
              moduleBlock: latestBlock,
              platformObjectJsonSchema: undefined,
              showErrorObjects,
            }),
          }),
        );
      }
      updateStep(newStep, true);
    },
    [step, modulesList, workflowSchema, updateStep, getBlock, dispatch, showErrorObjects],
  );

  const updateInputMappings = useCallback(
    (newInputConfigs: InputConfig[]) => {
      updateStep(
        {
          ...step,
          'input-mappings': newInputConfigs.map(({ key, field, value }) => ({
            'input-token': key,
            'method-chain': field,
            'raw-value': value,
          })),
        },
        true,
      );
    },
    [step, updateStep],
  );

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<
    'child-workflow-block-id' | 'input-mappings'
  >(errors);

  useEventBus('publish-module', () => {
    refetchModules();
  });

  if (isLoadingAll || blockLazyQuery.isLoading) {
    return (
      <>
        <Label size='md' weight='bold'>
          Module
        </Label>
        <Spacer height={1} />
        <ReactSelect value='' options={[]} placeholder='Loading...' disabled />
      </>
    );
  }

  return (
    <>
      <ParamLabel labelText='Module' htmlFor='step-config-module__select-input' weight='bold' />
      <ReactSelect
        id='step-config-module__select'
        value={selectedModule?.id}
        disabled={!editable}
        options={moduleOptions}
        onChange={updateWorkflowBlockId}
        placeholder='Select...'
        hasError={hasErrorFor('child-workflow-block-id')}
      />
      {renderErrorMessageFor('child-workflow-block-id')}
      {selectedModule?.attributes.description && (
        <>
          <Spacer height={2} />
          <Label id='step-config-module__description' size='md' weight='bold'>
            Description
          </Label>
          <Spacer height={1} />
          <Paragraph aria-labelledby='step-config-module__description'>
            {selectedModule.attributes.description}
          </Paragraph>
        </>
      )}
      {selectedModule && blockResponse && (
        <>
          <Spacer height={2} />
          <HStack
            align='center'
            justify={isLatestBlock ? 'left' : 'space-between'}
            spacing={Spacing[1]}
          >
            <Label id='step-config-module__version' size='md' weight='bold'>
              Version
            </Label>
            {isLatestBlock ? (
              <Sticker variant='success'>Latest</Sticker>
            ) : (
              <Button
                variant='basic'
                size='sm'
                width='auto'
                disabled={!editable}
                id='step-config-module__update-latest-block'
                aria-label='Update to latest block'
                prefixIcon={<RotateIcon />}
                onClick={() => updateWorkflowBlockId(selectedModule.id)}
              >
                Update to Latest
              </Button>
            )}
          </HStack>
          <Spacer height={1} />
          {isFetchingBlock ? (
            'Loading...'
          ) : (
            <div aria-labelledby='step-config-module__version'>
              <Paragraph>
                Published:{' '}
                {DateTime.fromISO(blockResponse.data.attributes['updated-at']).toFormat(
                  DATE_AND_TIME_TZ_FORMAT,
                )}
              </Paragraph>
              <Link
                to={`/dashboard/workflows/modules/${selectedModule.id}?version=${blockResponse.data.id}`}
              >
                {blockResponse.data.id}
              </Link>
            </div>
          )}
        </>
      )}
      <Spacer height={2} />
      {inputConfigs.length ? (
        <Accordion title='Inputs' defaultExpanded>
          <Spacer height={2} />
          <PlatformSchemaMappings
            title='Module Inputs'
            subtitle="Configure the data that will be passed from this workflow's context to the module"
            inputConfigs={inputConfigs}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            buttonText='Inputs'
            editable={editable}
            resettable
            placeholderEmptyText='No inputs configured'
            erroredKeyPrefix='input-mappings'
            errors={errors}
            onUpdateInputConfigs={updateInputMappings}
          />
          {renderErrorMessageFor('input-mappings')}
        </Accordion>
      ) : (
        <Paragraph>This module does not receive any inputs.</Paragraph>
      )}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={(step) => updateStep(step as WorkflowStepModule)}
        disabled={!editable}
      />
    </>
  );
};

export default StepConfigModule;
